import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Heart, HeartHandshake, Briefcase, Home as HouseIcon, Stethoscope, Building2, ShoppingBag, Wrench,
  UtensilsCrossed, GraduationCap, Palmtree, Car, Bike, Plane, TrainFront, Bus, Film, Camera, Scissors,
  Dumbbell, PawPrint, Baby, Blocks, PencilRuler, BedDouble, Store, Star, Church, PartyPopper, Landmark,
  Coins, LineChart, Trophy, Code, Package, PlayCircle, Wallet, Hash, IdCard, Calculator, Bell, Siren, FolderOpen, Beaker, Globe, ShieldCheck,
  Users, MessageCircle, PlusCircle, LayoutDashboard, Search as SearchIcon, MapPin, Sparkles, ChevronRight,
  Inbox as InboxIcon, Bot, Megaphone, Contact2, Rss, Store as StoreIcon, Heart as HeartIcon, ShieldAlert,
  School as SchoolIcon, ShoppingCart, PlayCircle as EntertainIcon, Zap, Truck, QrCode, Wand2, Briefcase as JobIcon, Home as PropIcon, TrendingUp, Fingerprint, Nfc,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLocation as useUserLocation } from "@/context/LocationContext";
import { api } from "@/lib/api";
import { fetchHub, resolveIcon } from "@/lib/hub";
import { ContextAssist } from "@/components/ContextAssist";
import NearbyServicesWidget from "@/components/NearbyServicesWidget";
import { WeatherCard } from "@/components/weather/WeatherWidgets";
import { CheckSquare } from "lucide-react";
import FloatingSlot from "@/components/FloatingSlot";

const rupees = (paise) => `₹${((paise || 0) / 100).toFixed(0)}`;

// App-id → Personal Map POI slug. Only apps in this map get the "Show on Map"
// pin badge. Keeps Home discovery lean and avoids linking to categories the
// map doesn't yet know about.
const APP_TO_POI = {
  // Marketplace
  "svc-parking":  "parking",
  "svc-laundry":  "laundry",
  "svc-vending":  "vending_machine",
  "svc-liquor":   "liquor",
  "svc-medical":  "medical",
  "food":         "restaurant",
  "hotels":       "hotel",
  "stores":       "shops",
  "shop":         "shops",
  // Health & Family
  "doctors":      "doctor",
  "hospital":     "hospital",
  "salon":        "salon",
  "baby":         "baby_care",
  "photo":        "photo_shop",
  // Travel
  "cab":          "cab",
  "flight":       "airport",
  "movie":        "cinema",
  // Utilities
  "car-services": "vehicle_service",
  "washrooms":    "washroom",
  "parcels":      "parcel",
  // Money & Community
  "jobs":         "jobs",
};

// Each app has: id, label, icon, gradient bg, and route (either direct or search-category)
// Grouped so the launcher reads like an intentional home screen — not a dumped list.
const GROUPS = [
  {
    title: "Marketplace",
    apps: [
      { id: "matrimonial", label: "Matrimonial", icon: Heart,             grad: "from-rose-500 to-pink-600",           to: "/search?category=matrimonial" },
      { id: "video-intros",label: "Video Intros",icon: Camera,            grad: "from-fuchsia-500 to-rose-700",        to: "/video-intros" },
      { id: "nfc-hub",     label: "NFC · RFID",  icon: Nfc,               grad: "from-emerald-600 to-teal-900",        to: "/nfc" },
      { id: "astrology",   label: "Astrology",   icon: Sparkles,          grad: "from-indigo-700 to-purple-900",       to: "/astrology" },
      { id: "svc-parking", label: "Parking",     icon: Store,             grad: "from-sky-600 to-blue-800",            to: "/services/parking" },
      { id: "svc-rental",  label: "Tool Rental", icon: Wrench,            grad: "from-orange-600 to-red-700",          to: "/services/equipment_rental" },
      { id: "svc-clean",   label: "Cleaning",    icon: Sparkles,          grad: "from-emerald-600 to-teal-800",        to: "/services/cleaning" },
      { id: "svc-pest",    label: "Pest Control", icon: Wrench,           grad: "from-amber-600 to-orange-800",        to: "/services/pest_control" },
      { id: "svc-water",   label: "RO Water",    icon: Sparkles,          grad: "from-blue-500 to-cyan-700",           to: "/services/water_ro" },
      { id: "svc-laundry", label: "Laundry",     icon: ShoppingBag,       grad: "from-cyan-600 to-blue-800",           to: "/services/laundry" },
      { id: "svc-vending", label: "Vending",     icon: Store,             grad: "from-fuchsia-600 to-purple-800",      to: "/services/vending_machine" },
      { id: "svc-saas",    label: "Micro-SaaS",  icon: Wrench,            grad: "from-slate-700 to-stone-900",         to: "/services/saas_micro" },
      { id: "svc-liquor",  label: "Liquor · Bars", icon: Store,           grad: "from-red-700 to-rose-900",            to: "/services/liquor_bar" },
      { id: "svc-medical", label: "Medical Store", icon: Stethoscope,     grad: "from-emerald-500 to-teal-800",        to: "/services/medical_store" },
      { id: "charity",     label: "Charity · Donate", icon: HeartIcon,    grad: "from-rose-600 to-red-800",            to: "/charity" },
      { id: "live-trains", label: "Live Trains",  icon: TrainFront,       grad: "from-sky-700 to-indigo-900",          to: "/live-trains" },
      { id: "locker",      label: "Digi Locker",  icon: ShieldCheck,      grad: "from-amber-600 to-rose-700",          to: "/locker" },
      { id: "tasks",       label: "Tasks",       icon: CheckSquare,       grad: "from-amber-500 to-orange-600",        to: "/tasks" },
      { id: "merchant-payouts", label: "Payouts", icon: Wallet,           grad: "from-emerald-700 to-green-900",       to: "/merchant/payouts" },
      { id: "dating",      label: "Dating",      icon: HeartHandshake,    grad: "from-pink-500 to-fuchsia-600",        to: "/dating" },
      { id: "jobs",        label: "Jobs · Rare",  icon: Briefcase,         grad: "from-blue-600 to-indigo-700",         to: "/jobs" },
      { id: "property",    label: "Property",    icon: HouseIcon,         grad: "from-emerald-600 to-teal-700",        to: "/search?category=property" },
      { id: "rentals",     label: "Rentals",     icon: Bike,              grad: "from-cyan-500 to-blue-600",           to: "/search?category=rental" },
      { id: "shop",        label: "Shopping",    icon: ShoppingBag,       grad: "from-orange-500 to-red-600",          to: "/search?category=product" },
      { id: "stores",      label: "Stores",      icon: Store,             grad: "from-amber-500 to-orange-600",        to: "/search?category=store" },
      { id: "brands",      label: "Brands",      icon: Star,              grad: "from-yellow-500 to-amber-600",        to: "/search?category=brand" },
      { id: "food",        label: "Food",        icon: UtensilsCrossed,   grad: "from-red-500 to-rose-600",            to: "/search?category=restaurant" },
      { id: "hotels",      label: "Hotels",      icon: BedDouble,         grad: "from-teal-500 to-emerald-600",        to: "/search?category=hotel" },
      { id: "vacation",    label: "Vacation",    icon: Palmtree,          grad: "from-sky-500 to-cyan-600",            to: "/search?category=vacation" },
      { id: "services",    label: "Services",    icon: Wrench,            grad: "from-slate-600 to-slate-800",         to: "/search?category=service" },
    ],
  },
  {
    title: "Health & Family",
    apps: [
      { id: "doctors",     label: "Doctors",     icon: Stethoscope,       grad: "from-emerald-500 to-green-700",       to: "/search?category=doctor" },
      { id: "hospital",    label: "Hospitals",   icon: Building2,         grad: "from-red-600 to-rose-700",            to: "/search?category=hospital" },
      { id: "physio",      label: "Physio",      icon: Sparkles,          grad: "from-lime-500 to-emerald-600",        to: "/search?category=physiotherapy" },
      { id: "baby",        label: "Baby Care",   icon: Baby,              grad: "from-pink-400 to-rose-500",           to: "/search?category=baby_care" },
      { id: "daycare",     label: "Daycare",     icon: Blocks,            grad: "from-fuchsia-500 to-pink-600",        to: "/search?category=daycare" },
      { id: "tutor",       label: "Tutors",      icon: PencilRuler,       grad: "from-violet-500 to-purple-700",       to: "/search?category=tutor" },
      { id: "elder",       label: "Elder Care",  icon: HeartHandshake,    grad: "from-amber-600 to-orange-700",        to: "/search?category=elder_care" },
      { id: "salon",       label: "Salon",       icon: Scissors,          grad: "from-purple-500 to-fuchsia-600",      to: "/search?category=salon" },
      { id: "gym",         label: "Gym",         icon: Dumbbell,          grad: "from-stone-700 to-black",             to: "/search?category=gym" },
      { id: "pet",         label: "Pet Care",    icon: PawPrint,          grad: "from-yellow-600 to-amber-700",        to: "/search?category=pet_care" },
      { id: "photo",       label: "Photographer",icon: Camera,            grad: "from-neutral-700 to-neutral-900",     to: "/search?category=photographer" },
    ],
  },
  {
    title: "Travel & Events",
    apps: [
      { id: "cab",         label: "Cab",         icon: Car,               grad: "from-yellow-500 to-amber-600",        to: "/search?category=cab" },
      { id: "flight",      label: "Flights",     icon: Plane,             grad: "from-sky-600 to-blue-700",            to: "/flights" },
      { id: "train",       label: "Trains",      icon: TrainFront,        grad: "from-blue-700 to-indigo-800",         to: "/search?category=train" },
      { id: "bus",         label: "Buses",       icon: Bus,               grad: "from-orange-600 to-red-700",          to: "/search?category=bus" },
      { id: "wedding",     label: "Weddings",    icon: Church,            grad: "from-rose-600 to-pink-700",           to: "/search?category=marriage_hall" },
      { id: "party",       label: "Party Halls", icon: PartyPopper,       grad: "from-purple-600 to-fuchsia-700",      to: "/search?category=party_hall" },
      { id: "movie",       label: "Movies",      icon: Film,              grad: "from-red-700 to-rose-800",            to: "/search?category=movie" },
    ],
  },
  {
    title: "Money & Identity",
    apps: [
      { id: "wallet",      label: "Wallet",      icon: Wallet,            grad: "from-emerald-600 to-green-800",       to: "/hub#wallet" },
      { id: "life",        label: "Life Dash",   icon: LayoutDashboard,   grad: "from-[#2D5A27] via-emerald-700 to-teal-800", to: "/life" },
      { id: "smart-dash",  label: "Smart Dash",  icon: Sparkles,          grad: "from-fuchsia-600 via-rose-600 to-amber-600", to: "/dashboard-smart" },
      { id: "community",   label: "Pay ♥ / Suggest",   icon: HeartIcon,       grad: "from-rose-500 via-pink-600 to-fuchsia-700", to: "/community" },
      { id: "nvn",         label: "NVN ID",      icon: Hash,              grad: "from-stone-800 to-stone-900",         to: "/hub#nvn" },
      { id: "card",        label: "Biz Card",    icon: IdCard,            grad: "from-[#E05E36] to-[#c94e2a]",         to: "/hub#card" },
      { id: "erp",         label: "Ledger",      icon: Calculator,        grad: "from-teal-700 to-emerald-900",        to: "/erp" },
      { id: "files",       label: "Files",       icon: FolderOpen,        grad: "from-sky-600 to-indigo-800",          to: "/files" },
      { id: "lab",         label: "AI Lab",      icon: Beaker,            grad: "from-fuchsia-600 to-purple-800",      to: "/lab" },
      { id: "visa",        label: "Visa Apply",  icon: Globe,             grad: "from-emerald-600 to-teal-900",        to: "/visa" },
      { id: "foundation",  label: "Foundation",  icon: HeartIcon,         grad: "from-[#E05E36] to-rose-800",          to: "/foundation" },
      { id: "yojna",       label: "Govt Yojna",  icon: Landmark,          grad: "from-blue-800 to-slate-900",          to: "/search?category=yojna" },
      { id: "loan",        label: "Loans",       icon: Landmark,          grad: "from-green-700 to-emerald-800",       to: "/search?category=loan" },
      { id: "invest",      label: "Invest",      icon: LineChart,         grad: "from-emerald-500 to-teal-700",        to: "/search?category=investment" },
      { id: "metal",       label: "Metal Pocket",icon: Coins,             grad: "from-amber-500 to-yellow-700",        to: "/metal-pocket" },
      { id: "gold-silver", label: "Gold/Silver Auto",icon: Coins,         grad: "from-yellow-500 to-amber-700",        to: "/wallet/gold-silver" },
      { id: "wuid",        label: "WUID",        icon: Fingerprint,       grad: "from-emerald-700 to-teal-900",        to: "/wuid" },
      { id: "prices",      label: "Prices",      icon: LineChart,         grad: "from-lime-600 to-emerald-700",        to: "/prices" },
      { id: "antique",     label: "Antiques",    icon: Trophy,            grad: "from-amber-700 to-yellow-800",        to: "/search?category=antique" },
    ],
  },
  {
    title: "Community",
    apps: [
      { id: "feed",        label: "Social Feed", icon: Rss,               grad: "from-blue-500 to-indigo-700",         to: "/feed" },
      { id: "messenger",   label: "Messenger",   icon: MessageCircle,     grad: "from-sky-500 to-blue-700",            to: "/messenger" },
      { id: "wishlist",    label: "Wishlist",    icon: HeartIcon,         grad: "from-rose-500 to-pink-700",           to: "/wishlist" },
      { id: "store-mine",  label: "My Store",    icon: StoreIcon,         grad: "from-amber-500 to-orange-700",        to: "/store/me" },
      { id: "cart",        label: "Cart",        icon: ShoppingCart,      grad: "from-teal-500 to-emerald-700",        to: "/cart" },
      { id: "complaint",   label: "Complaint",   icon: ShieldAlert,       grad: "from-red-600 to-red-900",             to: "/complaint" },
      { id: "civic-portals",label: "Govt Portals",icon: Landmark,         grad: "from-blue-700 to-indigo-900",         to: "/civic-portals" },
      { id: "gov",          label: "Gov Services",icon: Landmark,          grad: "from-[#2D5A27] to-emerald-900",       to: "/gov" },
      { id: "products",     label: "B2B Equip",   icon: Package,           grad: "from-amber-600 to-orange-800",        to: "/products" },
      { id: "off-onboard",  label: "Officials",   icon: ShieldCheck,       grad: "from-slate-700 to-blue-900",          to: "/officials/onboard" },
      { id: "schools",      label: "Schools",     icon: SchoolIcon,        grad: "from-blue-600 to-indigo-800",         to: "/schools",        keywords: ["school", "parents", "parent portal", "education", "board", "cbse", "icse", "join school", "register school", "students", "teachers", "admin"] },
      { id: "parent-portal",label: "Parents Portal", icon: GraduationCap,  grad: "from-indigo-600 to-violet-800",       to: "/school",         keywords: ["parent portal", "parents portal", "ward", "monitor ward", "monitor child", "child activities", "school activities", "attendance", "homework", "fees", "timetable", "class journal", "e-learning", "school updates", "school notice"] },
      { id: "volunteer",    label: "City Builder",icon: HeartIcon,         grad: "from-rose-600 to-pink-800",           to: "/volunteer" },
      { id: "contacts",    label: "My Contacts", icon: Contact2,          grad: "from-indigo-500 to-blue-800",         to: "/contacts" },
      { id: "brands",      label: "Brand Auth",  icon: ShieldAlert,       grad: "from-rose-600 to-red-900",            to: "/brands" },
      { id: "clubs",       label: "Clubs",       icon: Users,             grad: "from-amber-600 to-yellow-800",        to: "/clubs" },
      { id: "onboarding-ai",label:"AI Onboard",  icon: Sparkles,          grad: "from-fuchsia-600 to-pink-800",        to: "/onboarding-ai" },
      { id: "entertainment", label: "Entertain", icon: EntertainIcon,     grad: "from-fuchsia-600 to-purple-800",     to: "/entertainment" },
      { id: "micropay",    label: "MicroPay",    icon: Zap,               grad: "from-yellow-500 to-orange-700",       to: "/micropay" },
      { id: "delivery",    label: "Delivery",    icon: Truck,             grad: "from-orange-500 to-red-800",          to: "/delivery" },
      { id: "teacher-qr",  label: "Teacher QR",  icon: QrCode,            grad: "from-purple-600 to-indigo-800",       to: "/school/teacher" },
      { id: "autolist",    label: "AI Auto-Post",icon: Wand2,             grad: "from-fuchsia-600 to-pink-800",        to: "/autolist" },
      { id: "recruiter",   label: "Recruiter",   icon: JobIcon,           grad: "from-blue-600 to-slate-900",          to: "/recruiter" },
      { id: "property",    label: "Property",    icon: PropIcon,          grad: "from-cyan-600 to-teal-900",           to: "/property" },
      { id: "analytics",   label: "Analytics",   icon: TrendingUp,        grad: "from-violet-600 to-fuchsia-900",      to: "/analytics" },
      { id: "inbox",       label: "Inbox",       icon: InboxIcon,         grad: "from-[#E05E36] to-red-700",           to: "/inbox" },
      { id: "welfare",     label: "Welfare",     icon: HeartHandshake,    grad: "from-red-500 to-rose-700",            to: "/welfare" },
      { id: "donate",      label: "Donate",      icon: Heart,             grad: "from-rose-500 to-red-700",            to: "/donate" },
      { id: "citizens",    label: "Citizens",    icon: Users,             grad: "from-indigo-600 to-purple-700",       to: "/citizens" },
      { id: "messages",    label: "Messages",    icon: MessageCircle,     grad: "from-cyan-500 to-blue-700",           to: "/messages" },
      { id: "alerts",      label: "Alerts",      icon: Bell,              grad: "from-amber-500 to-orange-700",        to: "/alerts" },
      { id: "emergency",   label: "Emergency",   icon: Siren,             grad: "from-red-600 to-red-900",             to: "/emergency" },
      { id: "learn",       label: "Learn",       icon: PlayCircle,        grad: "from-purple-500 to-indigo-700",       to: "/learn" },
      { id: "dev",         label: "Developers",  icon: Code,              grad: "from-slate-700 to-slate-900",         to: "/search?category=developer" },
    ],
  },
  {
    title: "Utilities",
    apps: [
      { id: "car-services", label: "Car Services",icon: Car,               grad: "from-slate-700 to-blue-900",          to: "/car-services" },
      { id: "washrooms",    label: "Washrooms",   icon: Sparkles,          grad: "from-cyan-600 to-teal-800",           to: "/washrooms" },
      { id: "health",       label: "Health Track",icon: Stethoscope,       grad: "from-emerald-600 to-green-800",       to: "/health" },
      { id: "nearmeet",     label: "Near Meet",   icon: Users,             grad: "from-fuchsia-600 to-purple-800",      to: "/nearmeet" },
      { id: "net-worth",    label: "Net Worth",   icon: LineChart,         grad: "from-amber-600 to-yellow-800",        to: "/net-worth" },
      { id: "ivr",          label: "IVR Autobot", icon: MessageCircle,     grad: "from-emerald-600 to-teal-900",        to: "/ivr" },
      { id: "gst-filing",   label: "GST Filing",  icon: Calculator,        grad: "from-indigo-600 to-blue-900",         to: "/gst-filing" },
      { id: "ai-collage",  label: "AI Collage",  icon: Bot,               grad: "from-[#10a37f] via-[#4285f4] to-[#556cf3]", to: "/ai/compare" },
      { id: "ai-lead",     label: "AI Lead",     icon: Megaphone,         grad: "from-[#E05E36] via-[#DDA77B] to-[#2D5A27]", to: "/leads/new" },
      { id: "directory",   label: "Directory",   icon: Contact2,          grad: "from-blue-500 to-indigo-700",         to: "/directory" },
      { id: "search",      label: "Search",      icon: SearchIcon,        grad: "from-[#E05E36] to-orange-600",        to: "/search" },
      { id: "parcels",     label: "Parcels",     icon: Package,           grad: "from-blue-500 to-indigo-700",         to: "/parcels" },
      { id: "bookings",    label: "Bookings",    icon: LayoutDashboard,   grad: "from-stone-700 to-stone-900",         to: "/dashboard" },
      { id: "post",        label: "Post Ad",     icon: PlusCircle,        grad: "from-[#2D5A27] to-emerald-800",       to: "/post" },
      { id: "list-biz",    label: "List Biz",    icon: Store,             grad: "from-[#E05E36] to-red-700",           to: "/list-my-business" },
    ],
  },
];

export default function AppLauncher() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { loc } = useUserLocation();
  const [wallet, setWallet] = useState(null);
  const [nvn, setNvn] = useState(null);
  const [mktProjects, setMktProjects] = useState(null);   // null while loading; [] when empty
  const [q, setQ] = useState("");
  const [now, setNow] = useState(new Date());
  const [filter, setFilter] = useState("");
  // Universal Verticals Hub (Slice d) — server-driven catalogue.
  // `null` = loading, uses the hard-coded GROUPS fallback so first paint
  // is instant. When the API returns, we hydrate the icon strings to
  // React components and swap the source of truth.
  const [apiGroups, setApiGroups] = useState(null);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    // Fetch the canonical hub catalogue from the backend. Silent fallback.
    let cancelled = false;
    fetchHub().then(res => {
      if (cancelled || !res?.groups) return;
      // Hydrate: `icon` arrives as a string; resolve to a Lucide component.
      const hydrated = res.groups.map(g => ({
        ...g,
        apps: g.apps.map(a => ({ ...a, icon: resolveIcon(a.icon) })),
      }));
      setApiGroups(hydrated);
    });
    return () => { cancelled = true; };
  }, []);

  // Live source of truth: prefer backend catalogue, fall back to baked-in.
  const effectiveGroups = apiGroups || GROUPS;

  useEffect(() => {
    if (!user) return;
    api.get("/wallet").then(r => setWallet(r.data)).catch(() => {});
    api.get("/nvn/me").then(r => setNvn(r.data)).catch(() => setNvn(null));
    // Marketplace project count — powers the Signed-in CTA card below.
    // Silent fallback: if the endpoint is unreachable, we hide the card.
    api.get("/marketplace/projects", { params: { limit_n: 5 } })
      .then(r => setMktProjects(r.data?.projects || []))
      .catch(() => setMktProjects([]));
  }, [user]);

  const submitSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    navigate(`/search?${params.toString()}`);
  };

  const greeting = useMemo(() => {
    const h = now.getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    if (h < 21) return "Good evening";
    return "Good night";
  }, [now]);

  const timeStr = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const dateStr = now.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });

  // Flat filtered list when the user types in the search box
  const filteredApps = useMemo(() => {
    if (!filter.trim()) return null;
    const q = filter.trim().toLowerCase();
    const flat = effectiveGroups.flatMap(g => g.apps);
    return flat.filter(a =>
      a.label.toLowerCase().includes(q) ||
      a.id.toLowerCase().includes(q) ||
      (a.keywords || []).some(k => k.toLowerCase().includes(q))
    );
  }, [filter]);

  return (
    <div
      className="min-h-[calc(100vh-4rem)] relative overflow-hidden"
      style={{
        background: "radial-gradient(1200px 700px at 20% -10%, rgba(224,94,54,0.18), transparent 60%), radial-gradient(1000px 600px at 90% 10%, rgba(45,90,39,0.22), transparent 60%), linear-gradient(180deg, #0f1420 0%, #1a2033 55%, #0d1119 100%)",
      }}
      data-testid="app-launcher"
    >
      {/* subtle grain */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.07] mix-blend-overlay" style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")" }} />

      <div className="relative max-w-6xl mx-auto px-4 md:px-8 pt-6 pb-24">
        {/* Status header: greeting + clock + location */}
        <div className="flex items-start justify-between text-white/90">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-white/60">{dateStr}</div>
            <div className="heading text-3xl md:text-4xl font-black mt-1">
              {greeting},{" "}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#E05E36] to-[#DDA77B]">
                {user?.name?.split(" ")[0] || "friend"}
              </span>
            </div>
            {loc?.city && (
              <div className="mt-1 inline-flex items-center gap-1.5 text-xs text-white/60">
                <MapPin size={12} /> {loc.city}{loc.source === "gps" && <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
              </div>
            )}
          </div>
          <div className="text-right text-white/80">
            <div className="font-mono text-4xl md:text-5xl font-black tabular-nums leading-none">{timeStr}</div>
          </div>
        </div>

        {/* Widget row: Wallet + NVN + Ledger shortcut + live weather */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <WeatherCard
            lat={typeof loc?.lat === "number" ? loc.lat : 19.076}
            lng={typeof loc?.lng === "number" ? loc.lng : 72.8777}
            cityLabel={loc?.city}
          />
          <Link
            to="/hub#wallet"
            className="group rounded-3xl p-4 bg-gradient-to-br from-emerald-500/90 to-emerald-800/90 border border-white/10 backdrop-blur text-white shadow-xl hover:-translate-y-0.5 transition-transform"
            data-testid="widget-wallet"
          >
            <div className="flex items-center justify-between">
              <Wallet size={18} className="opacity-90" />
              <span className="text-[10px] uppercase tracking-widest text-white/70 font-bold">Wallet</span>
            </div>
            <div className="mt-2 text-3xl font-black tabular-nums">{wallet ? rupees(wallet.total_available_paise) : "—"}</div>
            <div className="mt-1 text-[11px] text-white/70">Tap to top up · ₹1 per action</div>
          </Link>

          <Link
            to="/hub#nvn"
            className="group rounded-3xl p-4 bg-gradient-to-br from-stone-800/90 to-stone-950/90 border border-white/10 backdrop-blur text-white shadow-xl hover:-translate-y-0.5 transition-transform"
            data-testid="widget-nvn"
          >
            <div className="flex items-center justify-between">
              <Hash size={18} className="opacity-90" />
              <span className="text-[10px] uppercase tracking-widest text-white/70 font-bold">NVN</span>
            </div>
            <div className="mt-2 font-mono font-black tabular-nums text-lg tracking-widest truncate">
              {nvn?.nvn ? nvn.nvn.replace(/(\d{3})(\d{3})(\d{3})(\d{3})/, "$1·$2·$3·$4") : "Claim now"}
            </div>
            <div className="mt-1 text-[11px] text-white/70">Your 12-digit universal ID</div>
          </Link>

          <Link
            to="/erp"
            className="hidden md:block group rounded-3xl p-4 bg-gradient-to-br from-teal-600/90 to-emerald-900/90 border border-white/10 backdrop-blur text-white shadow-xl hover:-translate-y-0.5 transition-transform"
            data-testid="widget-ledger"
          >
            <div className="flex items-center justify-between">
              <Calculator size={18} className="opacity-90" />
              <span className="text-[10px] uppercase tracking-widest text-white/70 font-bold">Ledger</span>
            </div>
            <div className="mt-2 text-lg font-black">Peer-to-peer bills</div>
            <div className="mt-1 text-[11px] text-white/70">Issue · Settle · Day-book</div>
          </Link>
        </div>

        {/* Signed-in Marketplace CTA — shows the customer's active project(s)            if any, otherwise offers a friendly "Describe what you want" nudge.
            Rendered inline (not a modal) so returning customers can jump
            straight into their in-flight project's detail page. */}
        {mktProjects !== null && (
          <Link
            to={mktProjects.length > 0
                ? `/marketplace/${mktProjects[0].project_id}`
                : "/marketplace"}
            className="mt-4 block group rounded-3xl overflow-hidden border border-[#E05E36]/40 bg-gradient-to-br from-[#E05E36]/95 via-[#c94e2a]/95 to-[#a83f21]/95 p-4 sm:p-5 hover:from-[#c94e2a] hover:to-[#8f341b] transition-colors shadow-xl"
            data-testid="applauncher-marketplace-cta"
          >
            <div className="flex items-start gap-4">
              <div className="hidden sm:grid place-items-center w-12 h-12 rounded-2xl bg-white/15 border border-white/25 shrink-0">
                <Sparkles size={20} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest font-bold text-white/70">
                  {mktProjects.length > 0 ? "Your software marketplace" : "New · Software marketplace"}
                </div>
                <div className="heading text-white font-black text-lg mt-1 leading-tight">
                  {mktProjects.length > 0
                    ? `You have ${mktProjects.length} ${mktProjects.length === 1 ? "project" : "projects"} in the pipeline`
                    : "Describe what you want — we'll build it."}
                </div>
                <div className="text-white/85 text-[12px] mt-1 leading-snug">
                  {mktProjects.length > 0
                    ? (
                        <>
                          Latest: <span className="font-bold text-white">
                            {(mktProjects[0].title || "").slice(0, 60) || "Untitled project"}
                          </span>
                          {mktProjects[0].status ? ` · ${mktProjects[0].status.replace(/_/g, " ")}` : ""}
                        </>
                      )
                    : "Website, mobile app, business software — plain English is enough."}
                </div>
              </div>
              <div className="hidden sm:inline-flex items-center gap-1 text-white/95 font-bold text-sm shrink-0 group-hover:translate-x-0.5 transition-transform">
                {mktProjects.length > 0 ? "View my projects" : "Start"}
                <ChevronRight size={16} />
              </div>
            </div>
          </Link>
        )}

        {/* My Tools — universal shortcut launcher (per vision: doorway, not clone). */}
        <Link
          to="/tools"
          className="mt-3 group block rounded-3xl overflow-hidden border border-white/15 bg-gradient-to-br from-slate-800/80 to-slate-950/80 hover:from-slate-800 hover:to-slate-900 transition-colors p-4 sm:p-5 shadow-lg"
          data-testid="applauncher-mytools-cta"
        >
          <div className="flex items-start gap-4">
            <div className="hidden sm:grid place-items-center w-12 h-12 rounded-2xl bg-white/10 border border-white/20 shrink-0">
              <Sparkles size={20} className="text-cyan-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-bold text-cyan-300/80">My Tools</div>
              <div className="heading text-white font-black text-base sm:text-lg mt-1 leading-tight">
                One doorway to your digital world
              </div>
              <div className="text-white/70 text-[12px] mt-1 leading-snug">
                Add shortcuts to Gmail, ChatGPT, Excel, WhatsApp Web — anything you already use. Follows you across devices.
              </div>
            </div>
            <div className="hidden sm:inline-flex items-center gap-1 text-cyan-300 font-bold text-sm shrink-0 group-hover:translate-x-0.5 transition-transform">
              Open <ChevronRight size={16} />
            </div>
          </div>
        </Link>

        {/* Universal search */}
        <form
          onSubmit={submitSearch}
          className="mt-6 flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/15 p-1.5 pl-4"
        >
          <SearchIcon size={16} className="text-white/60" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setFilter(e.target.value); }}
            placeholder="Search apps or listings — 'plumber', 'matrimonial', '2BHK'…"
            className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-white/50 py-2"
            data-testid="launcher-search-input"
          />
          <button
            type="submit"
            className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a] text-white text-sm font-bold px-4 py-2 transition-colors"
            data-testid="launcher-search-btn"
          >
            Go
          </button>
        </form>

        {!filteredApps && <ContextAssist />}

        {/* Nearby services widget — shown when no filter is active */}
        {!filteredApps && (
          <div className="rounded-3xl bg-white/95 backdrop-blur border border-white/40 p-4 mt-4 shadow-lg text-stone-800">
            <NearbyServicesWidget/>
          </div>
        )}

        {/* App grid: filtered OR grouped */}
        {filteredApps ? (
          <Section title={`Results (${filteredApps.length})`}>
            {filteredApps.length === 0 ? (
              <div className="col-span-full text-center text-white/60 text-sm py-10">
                No apps match &quot;{filter}&quot;. Try Search →
              </div>
            ) : filteredApps.map((a, i) => <AppTile key={a.id} app={a} delay={i * 0.02} />)}
          </Section>
        ) : (
          effectiveGroups.map(g => (
            <Section key={g.title} title={g.title}>
              {g.apps.map((a, i) => <AppTile key={a.id} app={a} delay={i * 0.02} />)}
            </Section>
          ))
        )}

        {/* Footer hint */}
        <div className="mt-10 text-center text-[11px] text-white/40">
          Tap any icon to open its full app · Long-press on mobile for shortcuts (coming soon)
        </div>
      </div>

      {/* Bottom dock — Phone / Messenger / Contacts style, always accessible.
          Sits above the Layout tabbar via FloatingSlot bottom-banner slot.
          pointer-events-none on the outer wrapper so the surrounding gutter
          is click-through; the inner pill re-enables pointer events. */}
      <FloatingSlot
        zone="bottom-banner"
        order={2}
        size="fab"
        zIndex="banner"
        hideOnKeyboard={false}
        className="md:hidden pointer-events-none"
      >
        <div className="mx-auto max-w-xs bg-black/45 backdrop-blur-xl border border-white/15 rounded-full p-2 flex items-center justify-around shadow-2xl pointer-events-auto" data-testid="applauncher-dock">
          <Link to="/feed" className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-indigo-700 grid place-items-center shadow-lg" data-testid="dock-feed">
            <Rss size={20} className="text-white" />
          </Link>
          <Link to="/messenger" className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center shadow-lg" data-testid="dock-messenger">
            <MessageCircle size={20} className="text-white" />
          </Link>
          <Link to="/complaint" className="w-11 h-11 rounded-full bg-gradient-to-br from-red-500 to-red-700 grid place-items-center shadow-lg" data-testid="dock-complaint">
            <ShieldAlert size={20} className="text-white" />
          </Link>
          <Link to="/wishlist" className="w-11 h-11 rounded-full bg-gradient-to-br from-rose-500 to-pink-700 grid place-items-center shadow-lg" data-testid="dock-wishlist">
            <HeartIcon size={20} className="text-white" />
          </Link>
          <Link to="/cart" className="w-11 h-11 rounded-full bg-gradient-to-br from-amber-500 to-orange-700 grid place-items-center shadow-lg" data-testid="dock-cart">
            <ShoppingCart size={20} className="text-white" />
          </Link>
        </div>
      </FloatingSlot>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-[11px] uppercase tracking-[0.25em] font-black text-white/50">{title}</h2>
        <div className="h-px flex-1 mx-4 bg-gradient-to-r from-white/10 to-transparent" />
      </div>
      <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4 md:gap-5">
        {children}
      </div>
    </div>
  );
}

function AppTile({ app, delay = 0 }) {
  const Icon = app.icon;
  const isHash = app.to.includes("#");
  const poiSlug = APP_TO_POI[app.id];
  const navigate = useNavigate();

  const goToApp = () => {
    if (isHash) {
      // Hash links: preserve full-page nav so /hub#wallet scrolls into view.
      window.location.assign(app.to);
    } else {
      navigate(app.to);
    }
  };

  const Inner = (
    <motion.div
      initial={{ opacity: 0, y: 6, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay, duration: 0.28, ease: "easeOut" }}
      whileHover={{ y: -4, scale: 1.04 }}
      whileTap={{ scale: 0.94 }}
      className={`w-16 h-16 md:w-[68px] md:h-[68px] rounded-[22px] bg-gradient-to-br ${app.grad} shadow-[0_10px_30px_-8px_rgba(0,0,0,0.55)] ring-1 ring-white/10 grid place-items-center relative overflow-hidden`}
    >
      <span className="absolute inset-x-2 top-1 h-4 rounded-full bg-white/25 blur-md opacity-60 group-hover:opacity-90 transition-opacity" />
      <Icon size={28} className="relative text-white drop-shadow" strokeWidth={2.25} />
    </motion.div>
  );

  const label = <div className="text-[11px] md:text-xs font-bold text-center leading-tight max-w-[76px] truncate">{app.label}</div>;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={goToApp}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goToApp(); } }}
      data-testid={`applauncher-${app.id}`}
      className="group flex flex-col items-center gap-2 text-white/90 hover:text-white transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 rounded-2xl"
    >
      <div className="relative">
        {Inner}
        {poiSlug && (
          <Link
            to={`/nearby?category=${poiSlug}`}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            aria-label={`Show ${app.label} on Personal Map`}
            title={`Show ${app.label} on Personal Map`}
            data-testid={`applauncher-${app.id}-mapcta`}
            className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-white text-emerald-700 grid place-items-center shadow-lg ring-2 ring-emerald-500/40 hover:bg-emerald-50 hover:scale-110 transition-transform z-10"
          >
            <MapPin size={12} strokeWidth={2.75} />
          </Link>
        )}
      </div>
      {label}
    </div>
  );
}
